import {
  GAME_FORMATS,
  RATING_MODES,
  RATING_PRESET_WEIGHTS,
  ELO_CONFIG,
  MATCHES_FIELDS,
  SET_FIELDS,
  MATCH_TEAM_FIELDS,
} from '../domain/dataModel';

/**
 * @param {number} matchesPlayed
 * @returns {number}
 */
function _getK(matchesPlayed) {
  return matchesPlayed < ELO_CONFIG.K_THRESHOLD ? ELO_CONFIG.K_PROVISIONAL : ELO_CONFIG.K_STANDARD;
}

/**
 * @param {number} delta
 * @returns {number}
 */
function _clampDelta(delta) {
  return Math.round(
    Math.max(ELO_CONFIG.MIN_DELTA, Math.min(ELO_CONFIG.MAX_DELTA, delta))
  );
}

/**
 * @param {string} memberId
 * @param {number} delta
 * @param {Map<string, { rating: number, matchesPlayed: number }>} playerRatings
 * @returns {{ memberId: string, ratingBefore: number, ratingAfter: number, delta: number }}
 */
function _buildResult(memberId, delta, playerRatings) {
  const ratingBefore = playerRatings.get(memberId)?.rating ?? ELO_CONFIG.RATING_BASE;
  return {
    memberId,
    ratingBefore,
    ratingAfter: ratingBefore + delta,
    delta,
  };
}

/**
 * @param {string} memberId
 * @param {Map<string, { rating: number, matchesPlayed: number }>} playerRatings
 * @returns {number}
 */
function _getPlayerRating(memberId, playerRatings) {
  return playerRatings.get(memberId)?.rating ?? ELO_CONFIG.RATING_BASE;
}

/**
 * @param {string} memberId
 * @param {Map<string, { rating: number, matchesPlayed: number }>} playerRatings
 * @returns {number}
 */
function _getMatchesPlayed(memberId, playerRatings) {
  return playerRatings.get(memberId)?.matchesPlayed ?? 0;
}

/**
 * Assegna a ogni elemento la posizione media (1-based) per gruppi con stessa chiave; indice 0 = rank 1.
 * @template T
 * @param {T[]} sortedByRank - Ordinato dal migliore (rank 1) al peggiore
 * @param {(item: T) => string} getMemberId
 * @param {(item: T) => number|string} getKey - Chiave di ex aequo (stesso valore → stessa posizione media)
 * @returns {Map<string, number>} memberId → posizione media
 */
function _averageRanksFromSorted(sortedByRank, getMemberId, getKey) {
  const out = new Map();
  const n = sortedByRank.length;
  let i = 0;
  while (i < n) {
    const key = getKey(sortedByRank[i]);
    let j = i + 1;
    while (j < n && getKey(sortedByRank[j]) === key) j += 1;
    const rankStart = i + 1;
    const rankEnd = j;
    const avg = (rankStart + rankEnd) / 2;
    for (let k = i; k < j; k += 1) {
      out.set(getMemberId(sortedByRank[k]), avg);
    }
    i = j;
  }
  return out;
}

/**
 * Formato MATCH: due squadre fisse, set con score1/score2, vincitore per set poi spareggio game.
 * @param {Object} match
 * @param {Object} period
 * @param {Map<string, { rating: number, matchesPlayed: number }>} playerRatings
 * @param {number} peso
 * @returns {Array<{ memberId: string, ratingBefore: number, ratingAfter: number, delta: number }>}
 */
function _calcMatch(match, period, playerRatings, peso) {
  const sets = match[MATCHES_FIELDS.SETS] || [];

  if (sets.length === 0) {
    const raw = match[MATCHES_FIELDS.PARTICIPANTS];
    if (Array.isArray(raw) && raw.length === 4) {
      const ids = raw.map((p) => p?.memberId).filter(Boolean);
      if (ids.length === 4) {
        const teamA = [ids[0], ids[1]];
        const teamB = [ids[2], ids[3]];
        const results = [];
        for (const memberId of teamA) {
          results.push(_buildResult(memberId, 0, playerRatings));
        }
        for (const memberId of teamB) {
          results.push(_buildResult(memberId, 0, playerRatings));
        }
        return results;
      }
    }
    return [];
  }

  const first = sets[0];
  const t1 = first[SET_FIELDS.TEAM1] || {};
  const t2 = first[SET_FIELDS.TEAM2] || {};
  const teamA = [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2]].filter(Boolean);
  const teamB = [t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].filter(Boolean);

  if (teamA.length !== 2 || teamB.length !== 2) {
    return [];
  }

  let gamesWonA = 0;
  let gamesWonB = 0;
  let setsWonA = 0;
  let setsWonB = 0;

  for (const set of sets) {
    const s1 = Number(set[SET_FIELDS.SCORE1]) || 0;
    const s2 = Number(set[SET_FIELDS.SCORE2]) || 0;
    gamesWonA += s1;
    gamesWonB += s2;
    if (s1 > s2) setsWonA += 1;
    else if (s2 > s1) setsWonB += 1;
  }

  let winner = 'draw';
  if (setsWonA > setsWonB) winner = 'A';
  else if (setsWonB > setsWonA) winner = 'B';
  else if (gamesWonA > gamesWonB) winner = 'A';
  else if (gamesWonB > gamesWonA) winner = 'B';

  const totalGames = gamesWonA + gamesWonB;
  let marginRatio;
  if (totalGames === 0) {
    marginRatio = 0.5;
  } else if (winner === 'A') {
    marginRatio = gamesWonA / totalGames;
  } else if (winner === 'B') {
    marginRatio = gamesWonB / totalGames;
  } else {
    marginRatio = 0.5;
  }

  let Sa;
  let Sb;
  if (winner === 'A') {
    Sa = 0.5 + 0.5 * marginRatio;
    Sb = 1 - Sa;
  } else if (winner === 'B') {
    Sb = 0.5 + 0.5 * marginRatio;
    Sa = 1 - Sb;
  } else {
    Sa = 0.5;
    Sb = 0.5;
  }

  const ratingMode = period?.ratingMode ?? RATING_MODES.RESULT_ONLY;
  let Ea;
  let Eb;
  if (ratingMode === RATING_MODES.RESULT_ONLY) {
    Ea = 0.5;
    Eb = 0.5;
  } else {
    const ratingA =
      (_getPlayerRating(teamA[0], playerRatings) + _getPlayerRating(teamA[1], playerRatings)) / 2;
    const ratingB =
      (_getPlayerRating(teamB[0], playerRatings) + _getPlayerRating(teamB[1], playerRatings)) / 2;
    Ea = 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
    Eb = 1 - Ea;
  }

  const K_teamA =
    (_getK(_getMatchesPlayed(teamA[0], playerRatings)) +
      _getK(_getMatchesPlayed(teamA[1], playerRatings))) /
    2;
  const K_teamB =
    (_getK(_getMatchesPlayed(teamB[0], playerRatings)) +
      _getK(_getMatchesPlayed(teamB[1], playerRatings))) /
    2;

  const results = [];
  for (const memberId of teamA) {
    const delta = _clampDelta(K_teamA * (Sa - Ea) * peso);
    results.push(_buildResult(memberId, delta, playerRatings));
  }
  for (const memberId of teamB) {
    const delta = _clampDelta(K_teamB * (Sb - Eb) * peso);
    results.push(_buildResult(memberId, delta, playerRatings));
  }
  return results;
}

/**
 * Formato ROTATION (King): classifica per posizione finale con ex aequo → posizione media; score vs attesa uniforme E=1/N.
 * @param {Object} match
 * @param {Map<string, { rating: number, matchesPlayed: number }>} playerRatings
 * @param {number} peso
 * @returns {Array<{ memberId: string, ratingBefore: number, ratingAfter: number, delta: number }>}
 */
function _calcRotation(match, playerRatings, peso) {
  const participants = match[MATCHES_FIELDS.PARTICIPANTS] || [];
  const N = participants.length;
  if (N === 1) {
    throw new Error('ROTATION con un solo partecipante non è supportato');
  }
  if (N < 2) {
    return [];
  }

  const sorted = [...participants].sort((a, b) => {
    const pa = Number(a.position);
    const pb = Number(b.position);
    if (pa !== pb) return pa - pb;
    return String(a.memberId).localeCompare(String(b.memberId));
  });

  const positionByMember = _averageRanksFromSorted(
    sorted,
    (p) => p.memberId,
    (p) => Number(p.position)
  );

  const E = 1 / N;
  const denom = N - 1;
  const results = [];

  for (const p of participants) {
    const memberId = p.memberId;
    if (!memberId) continue;
    const position = positionByMember.get(memberId) ?? Number(p.position);
    const score = (N - position) / denom;
    const K = _getK(_getMatchesPlayed(memberId, playerRatings));
    const deltaBase = K * (score - E);
    const delta = _clampDelta(deltaBase * peso);
    results.push(_buildResult(memberId, delta, playerRatings));
  }
  return results;
}

/**
 * Raccoglie tutti i memberId presenti nei set (mini-match).
 * @param {Object[]} sets
 * @returns {Set<string>}
 */
function _collectMemberIdsFromCompetitionSets(sets) {
  const ids = new Set();
  for (const set of sets) {
    const team1 = set[SET_FIELDS.TEAM1] || {};
    const team2 = set[SET_FIELDS.TEAM2] || {};
    const p1 = team1[MATCH_TEAM_FIELDS.PLAYER1];
    const p2 = team1[MATCH_TEAM_FIELDS.PLAYER2];
    const p3 = team2[MATCH_TEAM_FIELDS.PLAYER1];
    const p4 = team2[MATCH_TEAM_FIELDS.PLAYER2];
    [p1, p2, p3, p4].forEach((id) => {
      if (id) ids.add(id);
    });
  }
  return ids;
}

/**
 * Formato COMPETITION: somma game vinti nei mini-match, classifica reale vs attesa (RESULT_ONLY o EXPECTED_PERFORMANCE).
 * @param {Object} match
 * @param {Object} period
 * @param {Map<string, { rating: number, matchesPlayed: number }>} playerRatings
 * @param {number} peso
 * @returns {Array<{ memberId: string, ratingBefore: number, ratingAfter: number, delta: number }>}
 */
function _calcCompetition(match, period, playerRatings, peso) {
  const sets = match[MATCHES_FIELDS.SETS] || [];
  const gameVinti = new Map();

  for (const set of sets) {
    const team1 = set[SET_FIELDS.TEAM1] || {};
    const team2 = set[SET_FIELDS.TEAM2] || {};
    const p1 = team1[MATCH_TEAM_FIELDS.PLAYER1];
    const p2 = team1[MATCH_TEAM_FIELDS.PLAYER2];
    const p3 = team2[MATCH_TEAM_FIELDS.PLAYER1];
    const p4 = team2[MATCH_TEAM_FIELDS.PLAYER2];
    const score1 = Number(set[SET_FIELDS.SCORE1]) || 0;
    const score2 = Number(set[SET_FIELDS.SCORE2]) || 0;
    if (p1) gameVinti.set(p1, (gameVinti.get(p1) || 0) + score1);
    if (p2) gameVinti.set(p2, (gameVinti.get(p2) || 0) + score1);
    if (p3) gameVinti.set(p3, (gameVinti.get(p3) || 0) + score2);
    if (p4) gameVinti.set(p4, (gameVinti.get(p4) || 0) + score2);
  }

  let memberIds = [..._collectMemberIdsFromCompetitionSets(sets)];
  const fromParticipants = (match[MATCHES_FIELDS.PARTICIPANTS] || [])
    .map((p) => p?.memberId)
    .filter(Boolean);
  for (const id of fromParticipants) {
    if (!gameVinti.has(id)) gameVinti.set(id, 0);
    if (!memberIds.includes(id)) memberIds.push(id);
  }

  const N = memberIds.length;
  if (N === 1) {
    throw new Error('COMPETITION con un solo partecipante non è supportato');
  }
  if (N < 2) {
    return [];
  }

  const sortedByGames = [...memberIds].sort((a, b) => {
    const ga = gameVinti.get(a) || 0;
    const gb = gameVinti.get(b) || 0;
    if (ga !== gb) return gb - ga;
    return String(a).localeCompare(String(b));
  });

  const realPositionByMember = _averageRanksFromSorted(
    sortedByGames,
    (id) => id,
    (id) => gameVinti.get(id) || 0
  );

  const denom = N - 1;
  const ratingMode = period?.ratingMode ?? RATING_MODES.RESULT_ONLY;
  const results = [];

  if (ratingMode === RATING_MODES.RESULT_ONLY) {
    const E = 1 / N;
    for (const memberId of memberIds) {
      const pos = realPositionByMember.get(memberId) ?? N;
      const scoreReale = (N - pos) / denom;
      const K = _getK(_getMatchesPlayed(memberId, playerRatings));
      const delta = _clampDelta(K * (scoreReale - E) * peso);
      results.push(_buildResult(memberId, delta, playerRatings));
    }
    return results;
  }

  const sortedByRating = [...memberIds].sort((a, b) => {
    const ra = _getPlayerRating(a, playerRatings);
    const rb = _getPlayerRating(b, playerRatings);
    if (ra !== rb) return rb - ra;
    return String(a).localeCompare(String(b));
  });

  const expectedPositionByMember = _averageRanksFromSorted(
    sortedByRating,
    (id) => id,
    (id) => _getPlayerRating(id, playerRatings)
  );

  for (const memberId of memberIds) {
    const posReale = realPositionByMember.get(memberId) ?? N;
    const posAttesa = expectedPositionByMember.get(memberId) ?? N;
    const scoreReale = (N - posReale) / denom;
    const scoreAtteso = (N - posAttesa) / denom;
    const K = _getK(_getMatchesPlayed(memberId, playerRatings));
    const delta = _clampDelta(K * (scoreReale - scoreAtteso) * peso);
    results.push(_buildResult(memberId, delta, playerRatings));
  }

  return results;
}

/**
 * Calcola gli aggiornamenti ELO per tutti i partecipanti di un match.
 * Non scrive su Firestore — restituisce solo i risultati del calcolo.
 *
 * @param {Object} match - Documento match completo (con gameFormat, sets, participants per ROTATION)
 * @param {Object} period - Documento period (con ratingMode, ratingPreset)
 * @param {Map<string, { rating: number, matchesPlayed: number }>} playerRatings
 *   Mappa memberId → { rating, matchesPlayed } per ogni partecipante
 * @returns {Array<{ memberId: string, ratingBefore: number, ratingAfter: number, delta: number }>}
 */
export function calculateEloUpdates(match, period, playerRatings) {
  const presetKey =
    period?.ratingPreset != null && RATING_PRESET_WEIGHTS[period.ratingPreset]
      ? period.ratingPreset
      : 'CLASSIC';
  const presetWeights = RATING_PRESET_WEIGHTS[presetKey];
  const format = match?.gameFormat;
  const peso =
    format != null && presetWeights[format] != null ? presetWeights[format] : presetWeights[GAME_FORMATS.MATCH] ?? 1;

  switch (format) {
    case GAME_FORMATS.MATCH:
      return _calcMatch(match, period, playerRatings, peso);
    case GAME_FORMATS.ROTATION:
      return _calcRotation(match, playerRatings, peso);
    case GAME_FORMATS.COMPETITION:
      return _calcCompetition(match, period, playerRatings, peso);
    default:
      throw new Error(`gameFormat non riconosciuto: ${String(format)}`);
  }
}
